import { scaleOrdinal } from 'd3-scale'
import { Record, Map, List } from 'immutable'
import { pickBy } from 'ramda'
import { GROUPINGS } from './access-consents'

const FLAG_NAMES = [
  'ACTIVE',
  'TPP_SUSPENDED',
  'TPP_CLIENT_SUSPENDED',
  'TPP_REVOKED',
]

const FLAGS = FLAG_NAMES.reduce(
  (groupings, grouping) =>
    Object.assign({}, groupings, { [grouping]: grouping }),
  {}
)

const removeUndefined = pickBy(prop => prop !== undefined)

const TPP = Record({
  tppId: 'TPP ID IS MISSING',
  clientId: 'CLIENT ID IS MISSING',
  clientName: 'the Service Provider',
  agentName: '',
  tppName: 'the Service Provider',
  clientDisplayName: '',
  flag: 'ACTIVE',
  consents: Map(),
  avatarColour: '#707070',
})

function tppFlagOptions(consents) {
  const withGrouping = grouping => access =>
    access.get('accessGrouping') === grouping

  if (consents.some(withGrouping(GROUPINGS.TPP_SUSPENDED))) {
    return { flag: 'TPP_SUSPENDED' }
  } else if (consents.some(withGrouping(GROUPINGS.TPP_CLIENT_SUSPENDED))) {
    return { flag: 'TPP_CLIENT_SUSPENDED' }
  } else if (consents.some(withGrouping(GROUPINGS.TPP_REVOKED))) {
    return { flag: 'TPP_REVOKED' }
  }
  return {}
}

function addAvatarColour(colourByName) {
  return name => ({
    avatarColour: colourByName(name),
  })
}

function buildRecordForTPP(avatarColourOptions) {
  return consents => {
    const { tppId, clientId, clientName, agentName, channelId } = consents
      .first()
      .toJS()

    const tppRecord = new TPP(
      removeUndefined({
        tppId,
        clientId,
        clientName,
        agentName,
        clientDisplayName: getClientNameText(clientName, agentName, channelId),
        consents: consents
          .groupBy(access => access.get('id'))
          .map(list => list.first()),
        ...tppFlagOptions(consents),
        ...avatarColourOptions(agentName || clientName),
      })
    )

    return tppRecord
  }
}

function getAvatarState(tpp) {
  switch (tpp.get('flag')) {
    case FLAGS.TPP_SUSPENDED:
    case FLAGS.TPP_CLIENT_SUSPENDED:
      return 'SUSPENDED'
    case FLAGS.TPP_REVOKED:
      return 'REVOKED'
    default:
      return ''
  }
}

function getPriorityLabel(flag) {
  switch (flag) {
    case FLAGS.TPP_SUSPENDED:
    case FLAGS.TPP_CLIENT_SUSPENDED:
      return 'Suspended'
    case FLAGS.TPP_REVOKED:
      return 'Revoked'
    default:
      return 'Active'
  }
}

const collateTPPs = (consents, avatarColours = List()) =>
  consents
    .groupBy(access => access.get('clientId'))
    .map(
      buildRecordForTPP(
        addAvatarColour(scaleOrdinal().range(avatarColours.toJS()))
      )
    )
    .sortBy(tpp => tpp.get('clientDisplayName'))

export function getClientName(
  data,
  defaultClientName = 'the Service Provider'
) {
  if (!data) {
    return defaultClientName
  }
  const clientName = data.get('clientName', defaultClientName)
  const agentName = data.get('agentName')
  const channelId = data.get('channelId', '')
  return getClientNameText(clientName, agentName, channelId)
}

export function getClientNameText(clientName, agentName, channelId = '') {
  if (channelId.includes('VRP') || channelId.includes('PISP')) {
    return agentName ? `${clientName} via ${agentName}` : clientName
  }
  return agentName ? `${agentName} on behalf of ${clientName}` : clientName
}

export function getMerchantName(
  data,
  defaultClientName = 'the Service Provider'
) {
  const merchantName = data.getIn(['aispDetails', 'merchantName'])
  const clientName = data.get('clientName', defaultClientName)
  return getMerchantNameText(merchantName, clientName)
}

function getMerchantNameText(merchantName, clientName) {
  return merchantName ? `${merchantName} via ${clientName}` : clientName
}

export { FLAGS, collateTPPs, getAvatarState, getPriorityLabel }
